// Generic "extra" table GET/save actions — BioExtra, ArrestReportsExtra, and
// any other per-client dynamic-column table reachable through the same
// /api/extra/{tableName}/{idColumn}/{id} (GET) and /api/extra/save (POST)
// endpoints the Warrants module already uses for WarrantsExtra (see
// saveAllWarrantsExtra in pages/Warrants/index.jsx).
import API_ENDPOINTS from "./api";
import api from "@/services/axios_custom";
import { isNonEmptyVariable, convertColumnName } from "./common_function";

// Coerce a form value down to something the generic extra-table save endpoint
// accepts, parsed according to the column's actual SQL data type (e.g.
// "int", "decimal(18,2)", "bit", "varchar(255)") — DynamicForm/antd hand
// back numeric and boolean columns as strings, so without this an int
// column would be saved as "12" instead of 12. Anything that isn't a
// primitive after parsing (a dayjs object that slipped through unformatted,
// an object, etc.) is dropped to null rather than sent as-is. Mirrors
// Warrants' local normalizeExtraValue (see pages/Warrants/index.jsx
// saveAllWarrantsExtra), extended with type-based parsing.
const normalizeExtraValue = (value, dataType = "") => {
  if (value === null || value === undefined || value === "") return null;

  const type = dataType?.toLowerCase() || "";

  // Integer columns (int, bigint, smallint, tinyint, ...).
  if (type.includes("int")) {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  // Decimal/floating-point columns.
  if (
    type.includes("float") ||
    type.includes("decimal") ||
    type.includes("numeric") ||
    type.includes("money") ||
    type.includes("real")
  ) {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  // Boolean columns.
  if (type.includes("bit") || type.includes("bool")) {
    if (typeof value === "boolean") return value ? "true" : "false";
    const lowered = String(value).toLowerCase().trim();
    if (["true", "1", "y", "yes"].includes(lowered)) return "true";
    if (["false", "0", "n", "no"].includes(lowered)) return "false";
    return value ? "true" : "false"; // fallback to truthy/falsy
  }

  if (
    typeof value === "boolean" ||
    typeof value === "number" ||
    typeof value === "string"
  ) {
    return value;
  }
  return null;
};

// Fetches the saved row for a generic "extra" table (BioExtra,
// ArrestReportsExtra, ...) — same GET /api/extra/{tableName}/{idColumn}/{id}
// endpoint the Warrants module uses for WarrantsExtra. Returns the raw API
// row (literal DB column names, e.g. "DNA_Test_Date_Completed_U") converted
// into camelCase keys matching convertApiFields' DynamicForm field names
// (convertColumnName), so callers can drop the result straight into
// form.setFieldsValue / redux without a separate translation step.
const fetchExtraTableRecord = async (tableName, idColumn, idValue) => {
  if (!isNonEmptyVariable(idValue)) return null;

  const url = `${API_ENDPOINTS.getExtraTableData}${tableName}/${idColumn}/${idValue}`;
  const response = await api.get(url);
  const data = response?.data;
  const raw = Array.isArray(data) ? data[data.length - 1] : data;
  if (!isNonEmptyVariable(raw)) return null;

  // Only convert the client-configurable "_U" columns — the table's own
  // system columns (BioExtraID, BioID, ...) are bookkeeping the caller
  // already handles separately and would otherwise collide under garbled
  // (non-camelCase) keys here, since convertColumnName only camelCases on
  // underscores.
  const values = Object.keys(raw).reduce((acc, key) => {
    if (key.endsWith("_U")) acc[convertColumnName(key)] = raw[key];
    return acc;
  }, {});

  return { raw, values };
};

// CivilPaperExtraFields.jsx (unlike AliasesList.jsx/AddressesForm.jsx, whose
// Extra sections use convertApiFields(tableData, tableName) to build
// genuinely camelCased Form field names) calls convertApiFields with
// skipColumnName=true, so its Form.Item names — and therefore the keys in
// whatever it hands to onExtraChange — are the *raw* SQL column names
// (e.g. "Court_Date_U"), matching /api/extra/{tableName}/{idColumn}/{id}'s
// GET response verbatim (confirmed against a real BioExtra GET response —
// it comes back as "Dominant_Hand_U" etc., not camelCase, so this was a
// deliberate, reasonable choice for that component's own read/display path).
// saveExtraTableRecord's camelToRaw map is keyed the other way (camelCase ->
// raw), built for callers whose extraRecord is already camelCased — handed
// a raw-keyed record instead, every lookup misses and no field ends up in
// the outgoing payload except whatever's set explicitly (idColumn). This
// converts a raw-keyed record to the camelCase saveExtraTableRecord expects,
// via the same convertColumnName it already uses internally, so the
// round-trip lines up.
const convertRawExtraRecordToCamelCase = (extraRecord) => {
  if (!extraRecord) return extraRecord;
  const converted = {};
  Object.entries(extraRecord).forEach(([key, value]) => {
    converted[convertColumnName(key)] = value;
  });
  return converted;
};

// Finds the extra table's own PK within an already-fetched extraRecord by
// the same robust suffix heuristic CivilPaperExtraFields.jsx's field-defs
// filter already uses (name.endsWith("extraid")), instead of guessing the
// exact key name — confirmed the hard way: CivilPapersNewExtra's real PK
// came back as "CivilPapersExtraID", not the naively-guessed
// "CivilPapersNewExtraID" (the table name minus "New", not the table name
// itself), and there's no reliable way to predict that pattern per table.
// Getting this key wrong doesn't break the save outright — the PK value
// still goes out fine as a regular field — but saveExtraTableRecord's
// CREATE-vs-UPDATE decision (extraRecord?.[extraIdKey]) silently defaults to
// CREATE every time, which looks exactly like "my edit never saved" once a
// duplicate row exists and whatever the GET endpoint returns for display
// isn't the new one.
const findExtraIdKey = (extraRecord) =>
  Object.keys(extraRecord ?? {}).find((key) => key.toLowerCase().endsWith("extraid"));

// Combines the two CivilPaperExtraFields.jsx-specific fixups above into one
// call: converts a raw-keyed extra record to what saveExtraTableRecord
// expects, and resolves its PK key (also converted, so it's actually
// present as a key on the converted record for the CREATE-vs-UPDATE check).
const buildCamelCaseExtraPayload = (rawExtraRecord) => {
  const rawIdKey = findExtraIdKey(rawExtraRecord);
  return {
    extraRecord: convertRawExtraRecordToCamelCase(rawExtraRecord),
    extraIdKey: rawIdKey ? convertColumnName(rawIdKey) : undefined,
  };
};

// Saves a single-record generic "extra" table (BioExtra, ArrestReportsExtra,
// ...) via POST /api/extra/save — the same endpoint/payload shape the
// Warrants module uses for WarrantsExtra (see saveAllWarrantsExtra in
// pages/Warrants/index.jsx), except this handles one record instead of a
// batch. `extraRecord` is expected to use convertColumnName-style camelCase
// keys (i.e. whatever's already sitting in redux from the form's onBlur) —
// this translates them back to the literal raw DB column names the backend
// expects by re-fetching the table's column defs and reversing
// convertColumnName. `extraIdKey` is the camelCase key of the extra table's
// own PK within extraRecord (e.g. "bioExtraID") — its presence (a truthy
// value) decides CREATE vs UPDATE.
const saveExtraTableRecord = async ({
  tableName,
  idColumn, // lower-first FK column name, e.g. "bioID" (matches the GET url's idColumn segment)
  idValue, // resolved parent id, e.g. bookings.bio[0].bioId
  extraRecord,
  extraIdKey,
}) => {
  if (!isNonEmptyVariable(idValue) || !isNonEmptyVariable(extraRecord)) {
    return null;
  }

  const fieldDefsRes = await api.get(
    `${API_ENDPOINTS.getTableColumns}/${tableName}`,
  );
  const camelToRaw = {};
  const columnTypes = {}; // raw column name -> SQL data type, e.g. "int"
  (fieldDefsRes?.data || []).forEach((d) => {
    if (!d?.columnName) return;
    camelToRaw[convertColumnName(d.columnName)] = d.columnName;
    columnTypes[d.columnName] = d.dataType;
  });

  const record = { [idColumn]: idValue };

  Object.entries(extraRecord).forEach(([key, value]) => {
    const rawKey = camelToRaw[key];
    if (!rawKey) return; // skip bookkeeping keys (extraIdKey, stray fields)
    // extraRecord can carry its own copy of the FK under whatever casing it
    // was originally fetched with (e.g. "CivilPaperID") — camelToRaw's
    // reverse lookup resolves that to a *different* key than the `idColumn`
    // already stamped above (e.g. "civilPaperID"), so without this check
    // the same value goes out twice under two different casings. idColumn
    // is already authoritative for this field; skip it here.
    if (rawKey.toLowerCase() === idColumn.toLowerCase()) return;
    record[rawKey] = normalizeExtraValue(value, columnTypes[rawKey]);
  });

  const operation = extraRecord?.[extraIdKey] ? "UPDATE" : "CREATE";

  return api.post(API_ENDPOINTS.saveExtraTable, {
    tableName,
    idColumn,
    records: [record],
    operation,
  });
};

// Saves the BioExtra / ArrestReportsExtra rows edited on the Personal
// Information / Arrest Report tabs via the generic /api/extra/save endpoint
// — the same one the Warrants module uses for WarrantsExtra (see
// saveAllWarrantsExtra in pages/Warrants/index.jsx). Bio and ArrestReports
// aren't nested under the main booking save payload, so this runs as a
// separate, best-effort call after the booking (and its resolved
// bio/arrestReport IDs) are confirmed saved. Shared by the Bookings (edit)
// and CreateBooking (new intake) save flows.
const saveBioAndArrestReportsExtra = async (
  bioExtraSnapshot,
  arrestReportsExtraSnapshot,
  freshBookings,
) => {
  const bioId = freshBookings?.bio?.[0]?.bioId ?? freshBookings?.bioId ?? null;
  const arrestReportId =
    freshBookings?.arrestReports?.[0]?.arrestReportId ?? null;

  const saves = [];

  if (isNonEmptyVariable(bioExtraSnapshot) && bioId) {
    saves.push(
      saveExtraTableRecord({
        tableName: "BioExtra",
        idColumn: "bioID",
        idValue: bioId,
        extraRecord: bioExtraSnapshot,
        extraIdKey: "bioExtraID",
      }),
    );
  }

  if (isNonEmptyVariable(arrestReportsExtraSnapshot) && arrestReportId) {
    saves.push(
      saveExtraTableRecord({
        tableName: "ArrestReportsExtra",
        idColumn: "arrestReportID",
        idValue: arrestReportId,
        extraRecord: arrestReportsExtraSnapshot,
        // Despite the table name being plural, the API serializes this PK
        // as singular "arrestReportExtraID" — see the matching note in
        // AdditionalBookingInfo.jsx.
        extraIdKey: "arrestReportExtraID",
      }),
    );
  }

  if (!saves.length) return;

  const results = await Promise.allSettled(saves);
  results.forEach((result) => {
    if (result.status === "rejected") {
      console.error(
        "[saveBioAndArrestReportsExtra] save failed:",
        result.reason,
      );
    }
  });
};

// Saves the InmatePropertyExtra row edited on the Inmate Property tabs
// (Property.jsx — "Property Taken From Inmate" / "Property List") via the
// same generic /api/extra/save endpoint. Not nested under the main booking
// save payload's actual write path (it's a separate table), so this runs as
// a separate, best-effort call after the booking (and its resolved
// inmatePropertyId) is confirmed saved — same shape as
// saveBioAndArrestReportsExtra above.
const saveInmatePropertyExtra = async (
  inmatePropertyExtraSnapshot,
  freshBookings,
) => {
  const inmatePropertyId =
    freshBookings?.inmateProperty?.[0]?.inmatePropertyId ?? null;

  if (!isNonEmptyVariable(inmatePropertyExtraSnapshot) || !inmatePropertyId) {
    return;
  }

  try {
    await saveExtraTableRecord({
      tableName: "InmatePropertyExtra",
      idColumn: "inmatePropertyID",
      idValue: inmatePropertyId,
      extraRecord: inmatePropertyExtraSnapshot,
      extraIdKey: "inmatePropertyExtraID",
    });
  } catch (error) {
    console.error("[saveInmatePropertyExtra] save failed:", error);
  }
};

// Saves BookingBailsExtra rows edited on the Bail Records tab
// (ChargesBailRecordsDetails.jsx / BailRecordDetailForm.jsx). Unlike Bio/
// ArrestReports/InmateProperty (exactly one row per booking), a booking can
// have several bail records — so this is a batch, one saveExtraTableRecord
// call per bail that actually has a pending bookingBailsExtra[0] snapshot,
// same idea as Warrants' saveAllWarrantsExtra.
//
// bookingBailsSnapshot is the pre-refresh bookings.bookingBails array (each
// entry carries its own bookingBailsExtra[0], stashed there by
// ChargesBailRecordsDetails.jsx's modifyBailDetailsAction). freshBookings is
// the post-save refetch. An existing bail already has its real
// bookingBailID; a brand new one only gets one once the booking is
// refetched, so it's resolved here by matching bookingBailUuid against
// freshBookings.bookingBails — the same client-generated correlation id
// EditChargesModal.jsx already relies on to link a charge to its bail.
const saveBookingBailsExtra = async (bookingBailsSnapshot, freshBookings) => {
  if (!Array.isArray(bookingBailsSnapshot) || !bookingBailsSnapshot.length) {
    return;
  }

  const freshBails = freshBookings?.bookingBails || [];

  const saves = bookingBailsSnapshot
    .filter((b) => isNonEmptyVariable(b?.bookingBailsExtra?.[0]))
    .map((b) => {
      const resolvedBailID =
        b?.bookingBailID ??
        freshBails.find((fb) => fb?.bookingBailUuid === b?.bookingBailUuid)
          ?.bookingBailID;

      if (!resolvedBailID) return null;

      return saveExtraTableRecord({
        tableName: "BookingBailsExtra",
        idColumn: "bookingBailID",
        idValue: resolvedBailID,
        extraRecord: b.bookingBailsExtra[0],
        extraIdKey: "bookingBailsExtraID",
      });
    })
    .filter(Boolean);

  if (!saves.length) return;

  const results = await Promise.allSettled(saves);
  results.forEach((result) => {
    if (result.status === "rejected") {
      console.error("[saveBookingBailsExtra] save failed:", result.reason);
    }
  });
};

// Saves HoldsExtra rows edited on the Holds tab (BookingHolds/
// BookingHoldsForm.jsx). Like BookingBailsExtra, a booking can have several
// holds, so this is a batch. Unlike BookingBailsExtra, Holds doesn't carry a
// stable client-side uuid the backend echoes back on refetch, so a brand new
// hold's holdsExtra can't be resolved post-save the way a new bail can —
// only holds that already had a real holdID *before* this save (i.e. edits,
// not new adds) get their extra fields saved here. A new hold's extra
// fields aren't captured on its first save, same disclosed limitation as
// this app's other list-child extra tables (Jail Incidents Involved
// Officers/Inmates, Work Crew Roster/BlackList, Inmate Billing).
const saveHoldsExtra = async (holdsSnapshot) => {
  if (!Array.isArray(holdsSnapshot) || !holdsSnapshot.length) return;

  const saves = holdsSnapshot
    .filter(
      (h) =>
        isNonEmptyVariable(h?.holdID) && isNonEmptyVariable(h?.holdsExtra?.[0]),
    )
    .map((h) =>
      saveExtraTableRecord({
        tableName: "HoldsExtra",
        idColumn: "holdID",
        idValue: h.holdID,
        extraRecord: h.holdsExtra[0],
        extraIdKey: "holdsExtraID",
      }),
    );

  if (!saves.length) return;

  const results = await Promise.allSettled(saves);
  results.forEach((result) => {
    if (result.status === "rejected") {
      console.error("[saveHoldsExtra] save failed:", result.reason);
    }
  });
};

// Saves InmateMovementRulesExtra rows edited on the Movement Rules tab
// (BookingsMovementRules.jsx). Like HoldsExtra, a booking can have several
// movement rules and no stable client-side uuid the backend echoes back on
// refetch, so only rules that already had a real inmateMovementRuleID
// *before* this save (i.e. edits, not new adds) get their extra fields
// saved here — same disclosed limitation as saveHoldsExtra.
const saveInmateMovementRulesExtra = async (movementRulesSnapshot) => {
  if (!Array.isArray(movementRulesSnapshot) || !movementRulesSnapshot.length) {
    return;
  }

  const saves = movementRulesSnapshot
    .filter(
      (r) =>
        isNonEmptyVariable(r?.inmateMovementRuleID) &&
        isNonEmptyVariable(r?.inmateMovementRulesExtra?.[0]),
    )
    .map((r) =>
      saveExtraTableRecord({
        tableName: "InmateMovementRulesExtra",
        idColumn: "inmateMovementRuleID",
        idValue: r.inmateMovementRuleID,
        extraRecord: r.inmateMovementRulesExtra[0],
        extraIdKey: "inmateMovementRuleExtraID",
      }),
    );

  if (!saves.length) return;

  const results = await Promise.allSettled(saves);
  results.forEach((result) => {
    if (result.status === "rejected") {
      console.error(
        "[saveInmateMovementRulesExtra] save failed:",
        result.reason,
      );
    }
  });
};

// Saves InmateTrackingExtra rows edited on the Housing and Tracking tab
// (InmateMovementDetails.jsx / HouseTrack.jsx). Like HoldsExtra/
// InmateMovementRulesExtra, a booking can have several tracking entries and
// no stable client-side uuid the backend echoes back on refetch (edits are
// matched by array index, not even a local uuid), so only entries that
// already had a real inmateTrackingID *before* this save (i.e. edits, not
// new adds) get their extra fields saved here — same disclosed limitation
// as saveHoldsExtra/saveInmateMovementRulesExtra.
const saveInmateTrackingExtra = async (inmateTrackingSnapshot) => {
  if (
    !Array.isArray(inmateTrackingSnapshot) ||
    !inmateTrackingSnapshot.length
  ) {
    return;
  }

  const saves = inmateTrackingSnapshot
    .filter(
      (t) =>
        isNonEmptyVariable(t?.inmateTrackingID) &&
        isNonEmptyVariable(t?.inmateTrackingExtra?.[0]),
    )
    .map((t) =>
      saveExtraTableRecord({
        tableName: "InmateTrackingExtra",
        idColumn: "inmateTrackingID",
        idValue: t.inmateTrackingID,
        extraRecord: t.inmateTrackingExtra[0],
        extraIdKey: "inmateTrackingExtraID",
      }),
    );

  if (!saves.length) return;

  const results = await Promise.allSettled(saves);
  results.forEach((result) => {
    if (result.status === "rejected") {
      console.error("[saveInmateTrackingExtra] save failed:", result.reason);
    }
  });
};

// Saves InmatePropertyItemsExtra rows edited on the Property List item
// modal (PropertyModal.jsx / Property.jsx). Like HoldsExtra/
// InmateMovementRulesExtra/InmateTrackingExtra, a booking can have several
// property items and no stable client-side uuid the backend echoes back on
// refetch, so only items that already had a real inmatePropertyItemId
// *before* this save (i.e. edits, not new adds) get their extra fields
// saved here — same disclosed limitation as the other list-child extras.
const saveInmatePropertyItemsExtra = async (inmatePropertyItemsSnapshot) => {
  if (
    !Array.isArray(inmatePropertyItemsSnapshot) ||
    !inmatePropertyItemsSnapshot.length
  ) {
    return;
  }

  const saves = inmatePropertyItemsSnapshot
    .filter(
      (i) =>
        isNonEmptyVariable(i?.inmatePropertyItemId) &&
        isNonEmptyVariable(i?.inmatePropertyItemsExtra?.[0]),
    )
    .map((i) =>
      saveExtraTableRecord({
        tableName: "InmatePropertyItemsExtra",
        idColumn: "inmatePropertyItemID",
        idValue: i.inmatePropertyItemId,
        extraRecord: i.inmatePropertyItemsExtra[0],
        // fieldsData.js and the ASP source disagree on singular vs plural
        // for this table's own PK — see the matching note in
        // PropertyModal.jsx. Leaning singular (InmatePropertyItemExtraID),
        // matching the ASP source's explicit DBSchema.KeyField and the
        // precedent set by ArrestReportsExtra's own singular PK.
        extraIdKey: "inmatePropertyItemExtraID",
      }),
    );

  if (!saves.length) return;

  const results = await Promise.allSettled(saves);
  results.forEach((result) => {
    if (result.status === "rejected") {
      console.error(
        "[saveInmatePropertyItemsExtra] save failed:",
        result.reason,
      );
    }
  });
};

// Saves AppointmentLogExtra rows edited on the Appointment Log tab
// (AppointmentLogDetails.jsx). Like HoldsExtra/InmateMovementRulesExtra/
// InmateTrackingExtra, a booking can have several appointment log entries
// and no stable client-side uuid the backend echoes back on refetch, so
// only entries that already had a real appointmentLogID *before* this save
// (i.e. edits, not new adds) get their extra fields saved here — same
// disclosed limitation as the other list-child extras.
const saveAppointmentLogExtra = async (appointmentLogSnapshot) => {
  if (!Array.isArray(appointmentLogSnapshot) || !appointmentLogSnapshot.length) {
    return;
  }

  const saves = appointmentLogSnapshot
    .filter(
      (a) =>
        isNonEmptyVariable(a?.appointmentLogID) &&
        isNonEmptyVariable(a?.appointmentLogExtra?.[0]),
    )
    .map((a) =>
      saveExtraTableRecord({
        tableName: "AppointmentLogExtra",
        idColumn: "appointmentLogID",
        idValue: a.appointmentLogID,
        extraRecord: a.appointmentLogExtra[0],
        extraIdKey: "appointmentLogExtraID",
      }),
    );

  if (!saves.length) return;

  const results = await Promise.allSettled(saves);
  results.forEach((result) => {
    if (result.status === "rejected") {
      console.error("[saveAppointmentLogExtra] save failed:", result.reason);
    }
  });
};

// Saves AddressesExtra rows edited on the Personal Information tab's List
// of addresses (AddressesList.jsx / AddressesForm.jsx, non-associates mode
// — i.e. the main Bio's own bioAddresses, not e.g. WarrantAffiant's
// associates-mode addresses, which aren't wired to a save flow here). Like
// HoldsExtra/InmateTrackingExtra, a booking can have several addresses and
// no stable client-side uuid the backend echoes back on refetch, so only
// addresses that already had a real addressId *before* this save (i.e.
// edits, not new adds) get their extra fields saved here — same disclosed
// limitation as the other list-child extras.
const saveBioAddressesExtra = async (bioAddressesSnapshot) => {
  if (!Array.isArray(bioAddressesSnapshot) || !bioAddressesSnapshot.length) {
    return;
  }

  const saves = bioAddressesSnapshot
    .filter(
      (ba) =>
        isNonEmptyVariable(ba?.addresses?.[0]?.addressId) &&
        isNonEmptyVariable(ba?.addresses?.[0]?.addressesExtra?.[0]),
    )
    .map((ba) =>
      saveExtraTableRecord({
        tableName: "AddressesExtra",
        idColumn: "addressID",
        idValue: ba.addresses[0].addressId,
        extraRecord: ba.addresses[0].addressesExtra[0],
        extraIdKey: "addressesExtraID",
      }),
    );

  if (!saves.length) return;

  const results = await Promise.allSettled(saves);
  results.forEach((result) => {
    if (result.status === "rejected") {
      console.error("[saveBioAddressesExtra] save failed:", result.reason);
    }
  });
};

// Saves the BusinessesExtra row edited on a Job Schedule Scheduled Event's
// Business Details section (ScheduledEventsDetails.jsx). A scheduled event
// has at most one business (unlike the list-child extras above), so this is
// a single-record save, like saveBioAndArrestReportsExtra — but the
// business's own id (businessId) is only known once it's already been
// saved at least once, so this only fires when editing an existing
// business, not a brand new one, same disclosed limitation as this app's
// other list-child extra tables.
const saveBusinessesExtra = async (scheduledEventsSnapshot) => {
  if (!Array.isArray(scheduledEventsSnapshot) || !scheduledEventsSnapshot.length) {
    return;
  }

  const saves = scheduledEventsSnapshot
    .map((se) => se?.scheduledEventsRecurring?.[0]?.workReleaseDestinations?.[0]?.businesses?.[0])
    .filter(
      (b) => isNonEmptyVariable(b?.businessId) && isNonEmptyVariable(b?.businessesExtra?.[0]),
    )
    .map((b) =>
      saveExtraTableRecord({
        tableName: "BusinessesExtra",
        idColumn: "businessID",
        idValue: b.businessId,
        extraRecord: b.businessesExtra[0],
        extraIdKey: "businessExtraID",
      }),
    );

  if (!saves.length) return;

  const results = await Promise.allSettled(saves);
  results.forEach((result) => {
    if (result.status === "rejected") {
      console.error("[saveBusinessesExtra] save failed:", result.reason);
    }
  });
};

// Saves the CashAccountingNewExtra row edited on the "Additional
// Information" section of the Cash Account tab (CashAccountDetails.jsx) via
// the generic /api/extra/save endpoint. Same shape as saveInmateMedicalExtra
// — cashAccountingNew is a normal onetoone entity on the booking (see
// bookingTree.js), so its base row is expected to come back nested under
// bookings.bookingCashAccount[0].cashAccountingNew[0] from the ordinary
// booking GET/save cycle, no separate resolve call for the parent id.
const saveCashAccountingNewExtra = async (
  cashAccountingNewExtraSnapshot,
  freshBookings,
) => {
  const cashAccountingId =
    freshBookings?.bookingCashAccount?.[0]?.cashAccountingNew?.[0]
      ?.cashAccountingId ?? null;

  if (!isNonEmptyVariable(cashAccountingNewExtraSnapshot) || !cashAccountingId) {
    return;
  }

  try {
    await saveExtraTableRecord({
      tableName: "CashAccountingNewExtra",
      idColumn: "cashAccountingID",
      idValue: cashAccountingId,
      extraRecord: cashAccountingNewExtraSnapshot,
      extraIdKey: "cashAccountingExtraID",
    });
  } catch (error) {
    console.error("[saveCashAccountingNewExtra] save failed:", error);
  }
};

// Saves CashAccountingNewTransactionsExtra rows edited on the Cash Account
// tab's Deposit/Purchase/Details modals (CashAccountDetails.jsx /
// CashAccountPaymentForm.jsx / CashAccountDetailForm.jsx). Like
// HoldsExtra/InmateTrackingExtra, a cash account can have several
// transactions and no stable client-side uuid the backend echoes back on
// refetch (existing transactions are even matched by receiptNumber, not an
// id, elsewhere in this flow), so only transactions that already had a real
// cashAccountingTransactionId *before* this save (i.e. edits via Details,
// not new Deposits/Purchases) get their extra fields saved here — same
// disclosed limitation as the other list-child extras.
const saveCashAccountingNewTransactionsExtra = async (
  cashAccountingNewTransactionsSnapshot,
) => {
  if (
    !Array.isArray(cashAccountingNewTransactionsSnapshot) ||
    !cashAccountingNewTransactionsSnapshot.length
  ) {
    return;
  }

  const saves = cashAccountingNewTransactionsSnapshot
    .filter(
      (t) =>
        isNonEmptyVariable(t?.cashAccountingTransactionId) &&
        isNonEmptyVariable(t?.cashAccountingNewTransactionsExtra?.[0]),
    )
    .map((t) =>
      saveExtraTableRecord({
        tableName: "CashAccountingNewTransactionsExtra",
        idColumn: "cashAccountingTransactionID",
        idValue: t.cashAccountingTransactionId,
        extraRecord: t.cashAccountingNewTransactionsExtra[0],
        extraIdKey: "cashAccountingTransactionExtraID",
      }),
    );

  if (!saves.length) return;

  const results = await Promise.allSettled(saves);
  results.forEach((result) => {
    if (result.status === "rejected") {
      console.error(
        "[saveCashAccountingNewTransactionsExtra] save failed:",
        result.reason,
      );
    }
  });
};

// Saves MedicationLogExtra rows edited on the Medication Log modal
// (InmateMedicationLogDetail.jsx). A booking can have several medications,
// each with several log entries — nested two levels deep — and no stable
// client-side uuid the backend echoes back on refetch, so only entries that
// already had a real medicationLogID *before* this save (i.e. edits, not
// new adds) get their extra fields saved here — same disclosed limitation
// as the other list-child extras.
const saveMedicationLogExtra = async (medicationsSnapshot) => {
  if (!Array.isArray(medicationsSnapshot) || !medicationsSnapshot.length) {
    return;
  }

  const saves = medicationsSnapshot
    .flatMap((m) => m?.medicationLog || [])
    .filter(
      (l) =>
        isNonEmptyVariable(l?.medicationLogID) &&
        isNonEmptyVariable(l?.medicationLogExtra?.[0]),
    )
    .map((l) =>
      saveExtraTableRecord({
        tableName: "MedicationLogExtra",
        idColumn: "medicationLogID",
        idValue: l.medicationLogID,
        extraRecord: l.medicationLogExtra[0],
        extraIdKey: "medicationLogExtraID",
      }),
    );

  if (!saves.length) return;

  const results = await Promise.allSettled(saves);
  results.forEach((result) => {
    if (result.status === "rejected") {
      console.error("[saveMedicationLogExtra] save failed:", result.reason);
    }
  });
};

// Saves ScheduledEventsExtra rows edited on the Scheduled Events tab
// (ScheduledEventsDetails.jsx). Like HoldsExtra/InmateMovementRulesExtra, a
// booking can have several scheduled events and no stable client-side uuid
// the backend echoes back on refetch, so only events that already had a
// real scheduledEventID *before* this save (i.e. edits, not new adds) get
// their extra fields saved here — same disclosed limitation as the other
// list-child extras.
const saveScheduledEventsExtra = async (scheduledEventsSnapshot) => {
  if (!Array.isArray(scheduledEventsSnapshot) || !scheduledEventsSnapshot.length) {
    return;
  }

  const saves = scheduledEventsSnapshot
    .filter(
      (se) =>
        isNonEmptyVariable(se?.scheduledEventID) &&
        isNonEmptyVariable(se?.scheduledEventsExtra?.[0]),
    )
    .map((se) =>
      saveExtraTableRecord({
        tableName: "ScheduledEventsExtra",
        idColumn: "scheduledEventID",
        idValue: se.scheduledEventID,
        extraRecord: se.scheduledEventsExtra[0],
        extraIdKey: "scheduledEventExtraID",
      }),
    );

  if (!saves.length) return;

  const results = await Promise.allSettled(saves);
  results.forEach((result) => {
    if (result.status === "rejected") {
      console.error("[saveScheduledEventsExtra] save failed:", result.reason);
    }
  });
};

// Saves the StaffExtra row edited on the "Additional Staff Information"
// section of the Staff module (AdditionalStaffInformation.jsx, rendered by
// StaffAdministrativeInformation.jsx) via the generic /api/extra/save
// endpoint. Same shape as saveInmateMedicalExtra — StaffExtra is a normal
// onetoone entity on the staff record (see RecordStructureStaff.asp's
// AddLink), so its base row is expected to come back nested under
// bookings.staffExtra[0] from the ordinary staff GET/save cycle, no
// separate resolve call for the parent id.
const saveStaffExtra = async (staffExtraSnapshot, freshBookings) => {
  const staffId = freshBookings?.staffId ?? null;

  if (!isNonEmptyVariable(staffExtraSnapshot) || !staffId) {
    return;
  }

  try {
    await saveExtraTableRecord({
      tableName: "StaffExtra",
      idColumn: "staffID",
      idValue: staffId,
      extraRecord: staffExtraSnapshot,
      extraIdKey: "staffExtraID",
    });
  } catch (error) {
    console.error("[saveStaffExtra] save failed:", error);
  }
};

// Saves VictimNotificationExtra rows edited on the Victim Notification tab
// (VictimNotificationDetails.jsx). Like HoldsExtra/AppointmentLogExtra, a
// booking can have several victim notifications and no stable client-side
// uuid the backend echoes back on refetch, so only notifications that
// already had a real victimNotificationID *before* this save (i.e. edits,
// not new adds) get their extra fields saved here — same disclosed
// limitation as the other list-child extras.
const saveVictimNotificationExtra = async (victimNotificationsSnapshot) => {
  if (
    !Array.isArray(victimNotificationsSnapshot) ||
    !victimNotificationsSnapshot.length
  ) {
    return;
  }

  const saves = victimNotificationsSnapshot
    .filter(
      (v) =>
        isNonEmptyVariable(v?.victimNotificationID) &&
        isNonEmptyVariable(v?.victimNotificationExtra?.[0]),
    )
    .map((v) =>
      saveExtraTableRecord({
        tableName: "VictimNotificationExtra",
        idColumn: "victimNotificationID",
        idValue: v.victimNotificationID,
        extraRecord: v.victimNotificationExtra[0],
        extraIdKey: "victimNotificationExtraID",
      }),
    );

  if (!saves.length) return;

  const results = await Promise.allSettled(saves);
  results.forEach((result) => {
    if (result.status === "rejected") {
      console.error(
        "[saveVictimNotificationExtra] save failed:",
        result.reason,
      );
    }
  });
};

// Saves VictimNotificationLogExtra rows edited on the Victim Notification
// Attempt modal (VictimNotificationAttemptForm.jsx /
// VictimNotificationDetails.jsx). A booking can have several victim
// notifications, each with several notification attempts — nested two
// levels deep — and no stable client-side uuid the backend echoes back on
// refetch, so only attempts that already had a real
// victimNotificationLogID *before* this save (i.e. edits, not new adds)
// get their extra fields saved here — same disclosed limitation as the
// other list-child extras.
const saveVictimNotificationLogExtra = async (victimNotificationsSnapshot) => {
  if (
    !Array.isArray(victimNotificationsSnapshot) ||
    !victimNotificationsSnapshot.length
  ) {
    return;
  }

  const saves = victimNotificationsSnapshot
    .flatMap((v) => v?.victimNotificationLog || [])
    .filter(
      (l) =>
        isNonEmptyVariable(l?.victimNotificationLogID) &&
        isNonEmptyVariable(l?.victimNotificationLogExtra?.[0]),
    )
    .map((l) =>
      saveExtraTableRecord({
        tableName: "VictimNotificationLogExtra",
        idColumn: "victimNotificationLogID",
        idValue: l.victimNotificationLogID,
        extraRecord: l.victimNotificationLogExtra[0],
        extraIdKey: "victimNotificationLogExtraID",
      }),
    );

  if (!saves.length) return;

  const results = await Promise.allSettled(saves);
  results.forEach((result) => {
    if (result.status === "rejected") {
      console.error(
        "[saveVictimNotificationLogExtra] save failed:",
        result.reason,
      );
    }
  });
};

// Saves the InmateMedicalExtra row edited on the "Additional Inmate Medical
// Info" tab (AdditionalInmateMedicalInfo.jsx) via the same generic
// /api/extra/save endpoint. Same shape as saveInmatePropertyExtra above —
// InmateMedical isn't nested under the main booking save payload's actual
// write path (it's a separate table), so this runs as a separate,
// best-effort call after the booking (and its resolved inmateMedicalId) is
// confirmed saved. InmateMedical itself is a normal onetoone entity on the
// booking (see bookingTree.js — same relation as bio/arrestReports), so its
// base row is expected to come back nested under bookings.inmateMedical[0]
// from the ordinary booking GET/save cycle, same as bio/arrestReports — no
// separate resolve call for the parent id, matching how BookingBails/
// BookingBailPayments only ever resolve their own ids from what's already
// in the booking's own state (see saveBookingBailsExtra), never a
// dedicated GET against their parent table.
const saveInmateMedicalExtra = async (
  inmateMedicalExtraSnapshot,
  freshBookings,
) => {
  const inmateMedicalId =
    freshBookings?.inmateMedical?.[0]?.inmateMedicalId ?? null;

  if (!isNonEmptyVariable(inmateMedicalExtraSnapshot) || !inmateMedicalId) {
    return;
  }

  try {
    await saveExtraTableRecord({
      tableName: "InmateMedicalExtra",
      idColumn: "inmateMedicalID",
      idValue: inmateMedicalId,
      extraRecord: inmateMedicalExtraSnapshot,
      extraIdKey: "inmateMedicalExtraID",
    });
  } catch (error) {
    console.error("[saveInmateMedicalExtra] save failed:", error);
  }
};

// Fetches BioExtra + ArrestReportsExtra + InmatePropertyExtra +
// InmateMedicalExtra for a freshly-loaded booking and merges them into its
// bio[0]/arrestReports[0]/inmateProperty[0]/inmateMedical[0], the same shape
// useBioExtraSync/useArrestReportsExtraSync/useInmatePropertyExtraSync/
// useInmateMedicalExtraSync (hooks/useExtraTableSync.js) populate into redux
// after the fact. Awaiting
// this right after the main booking GET (bookings/bookingNumber/id/{id})
// means the extra data is already present in the very same dispatch/render
// as the rest of the booking — no brief flash of empty extra fields while a
// follow-up GET catches up. Those hooks stay in place as a fallback for
// booking loads that don't go through this helper (and, since they gate on
// "do we already have the real PK", they're a no-op once this has already
// populated it).
const fetchAndMergeBookingExtras = async (bookingData) => {
  if (!bookingData) return bookingData;

  const bioId = bookingData?.bio?.[0]?.bioId ?? bookingData?.bioId ?? null;
  const arrestReportId =
    bookingData?.arrestReports?.[0]?.arrestReportId ?? null;
  const inmatePropertyId =
    bookingData?.inmateProperty?.[0]?.inmatePropertyId ?? null;
  const inmateMedicalId =
    bookingData?.inmateMedical?.[0]?.inmateMedicalId ?? null;

  const [bioResult, arrestResult, inmatePropertyResult, inmateMedicalResult] =
    await Promise.all([
      bioId
        ? fetchExtraTableRecord("BioExtra", "bioID", bioId).catch((error) => {
            console.error(
              "[fetchAndMergeBookingExtras] BioExtra fetch failed:",
              error,
            );
            return null;
          })
        : Promise.resolve(null),
      arrestReportId
        ? fetchExtraTableRecord(
            "ArrestReportsExtra",
            "arrestReportID",
            arrestReportId,
          ).catch((error) => {
            console.error(
              "[fetchAndMergeBookingExtras] ArrestReportsExtra fetch failed:",
              error,
            );
            return null;
          })
        : Promise.resolve(null),
      inmatePropertyId
        ? fetchExtraTableRecord(
            "InmatePropertyExtra",
            "inmatePropertyID",
            inmatePropertyId,
          ).catch((error) => {
            console.error(
              "[fetchAndMergeBookingExtras] InmatePropertyExtra fetch failed:",
              error,
            );
            return null;
          })
        : Promise.resolve(null),
      inmateMedicalId
        ? fetchExtraTableRecord(
            "InmateMedicalExtra",
            "inmateMedicalID",
            inmateMedicalId,
          ).catch((error) => {
            console.error(
              "[fetchAndMergeBookingExtras] InmateMedicalExtra fetch failed:",
              error,
            );
            return null;
          })
        : Promise.resolve(null),
    ]);

  const merged = { ...bookingData };

  if (bioResult && merged.bio?.length) {
    merged.bio = [
      {
        ...merged.bio[0],
        bioExtra: [
          {
            ...bioResult.values,
            bioExtraID: bioResult.raw?.BioExtraID ?? 0,
            bioID: bioId,
          },
        ],
      },
      ...merged.bio.slice(1),
    ];
  }

  if (arrestResult && merged.arrestReports?.length) {
    merged.arrestReports = [
      {
        ...merged.arrestReports[0],
        arrestReportsExtra: [
          {
            ...arrestResult.values,
            // Despite the table name being plural, the API serializes this
            // PK as singular "arrestReportExtraID" — see the matching note
            // in AdditionalBookingInfo.jsx.
            arrestReportExtraID: arrestResult.raw?.ArrestReportExtraID ?? 0,
            arrestReportID: arrestReportId,
          },
        ],
      },
      ...merged.arrestReports.slice(1),
    ];
  }

  if (inmatePropertyResult && merged.inmateProperty?.length) {
    merged.inmateProperty = [
      {
        ...merged.inmateProperty[0],
        inmatePropertyExtra: [
          {
            ...inmatePropertyResult.values,
            inmatePropertyExtraID:
              inmatePropertyResult.raw?.InmatePropertyExtraID ?? 0,
            inmatePropertyID: inmatePropertyId,
          },
        ],
      },
      ...merged.inmateProperty.slice(1),
    ];
  }

  if (inmateMedicalResult && inmateMedicalId) {
    merged.inmateMedical = [
      {
        ...(merged.inmateMedical?.[0] || {}),
        inmateMedicalId,
        inmateMedicalExtra: [
          {
            ...inmateMedicalResult.values,
            inmateMedicalExtraID:
              inmateMedicalResult.raw?.InmateMedicalExtraID ?? 0,
            inmateMedicalID: inmateMedicalId,
          },
        ],
      },
      ...(merged.inmateMedical?.slice(1) || []),
    ];
  }

  return merged;
};

// Saves the CivilPapersNewExtra row edited on the Civil Paper Details tab's
// "Additional Information" section via the generic /api/extra/save endpoint
// — same shape as saveInmateMedicalExtra/saveStaffExtra (a single onetoone
// extra table). Unlike those, Civil Papers has no Redux slice and no
// post-save refetch to resolve the parent id from yet, so this takes the
// civilPaperId directly rather than a freshBookings-style payload.
const saveCivilPapersNewExtra = async (
  civilPapersNewExtraSnapshot,
  civilPaperId,
) => {
  if (
    !isNonEmptyVariable(civilPapersNewExtraSnapshot) ||
    !isNonEmptyVariable(civilPaperId)
  ) {
    return;
  }
  try {
    await saveExtraTableRecord({
      tableName: "CivilPapersNewExtra",
      idColumn: "civilPaperID",
      idValue: civilPaperId,
      ...buildCamelCaseExtraPayload(civilPapersNewExtraSnapshot),
    });
  } catch (error) {
    console.error("[saveCivilPapersNewExtra] save failed:", error);
  }
};

// Saves the TrustAccountingNewExtra row edited on the Trust Account tab's
// "Additional Information" section. Same shape as saveCivilPapersNewExtra.
const saveTrustAccountingNewExtra = async (
  trustAccountingNewExtraSnapshot,
  trustAccountingId,
) => {
  if (
    !isNonEmptyVariable(trustAccountingNewExtraSnapshot) ||
    !isNonEmptyVariable(trustAccountingId)
  ) {
    return;
  }
  try {
    await saveExtraTableRecord({
      tableName: "TrustAccountingNewExtra",
      idColumn: "trustAccountingID",
      idValue: trustAccountingId,
      ...buildCamelCaseExtraPayload(trustAccountingNewExtraSnapshot),
    });
  } catch (error) {
    console.error("[saveTrustAccountingNewExtra] save failed:", error);
  }
};

// Saves CivilPapersPeopleExtra rows edited on each Case Association's
// "Agency-Specific Information" section. civilPaperDefendantId: null is this
// app's "not yet saved" sentinel for a brand-new person/business (see
// CivilPaperAssociations.jsx's writePeople — null, not 0, since the backend
// rejects a literal 0 on an id field meant to mean "create new"). Only
// people that already have a real civilPaperDefendantId get saved here; a
// brand-new one's extra fields are caught instead by
// saveNewCivilPapersPeopleExtras below, once a refetch resolves a real id.
const saveCivilPapersPeopleExtra = async (civilPapersPeopleSnapshot) => {
  if (!Array.isArray(civilPapersPeopleSnapshot) || !civilPapersPeopleSnapshot.length) {
    return;
  }

  const saves = civilPapersPeopleSnapshot
    .filter(
      (p) =>
        isNonEmptyVariable(p?.civilPaperDefendantId) &&
        isNonEmptyVariable(p?.civilPapersPeopleExtra?.[0]),
    )
    .map((p) =>
      saveExtraTableRecord({
        tableName: "CivilPapersPeopleExtra",
        idColumn: "civilPaperDefendantID",
        idValue: p.civilPaperDefendantId,
        ...buildCamelCaseExtraPayload(p.civilPapersPeopleExtra[0]),
      }),
    );

  if (!saves.length) return;
  const results = await Promise.allSettled(saves);
  results.forEach((result) => {
    if (result.status === "rejected") {
      console.error("[saveCivilPapersPeopleExtra] save failed:", result.reason);
    }
  });
};

// Catches CivilPapersPeopleExtra for a person/business just added to the
// civil paper (civilPaperDefendantId: null locally until this save
// round-trips a real one) — saveCivilPapersPeopleExtra above deliberately skips these
// since there's no real id yet to save against. Matched to the freshly
// refetched civilPapersPeople by bioId (person) or personToServeBusinessId
// (business — the business actually being served, NOT jacketBusinessId;
// see CaseAssociationBusinessInformation.jsx's own note) instead of array
// position — both are real, stable Jacket-system ids already assigned
// before this save and unaffected by it, unlike civilPaperDefendantId
// itself, which only this save resolves.
//
// A person copied from an existing folder has no bioId locally either (sent
// as null so the backend creates their own Bio — see handleFolderSelected in
// CivilPaperAssociations.jsx), so those fall back to jacketId, restricted to
// fresh people that weren't already on the record before this save, each
// claimed at most once.
const createFreshPersonMatcher = (
  civilPapersPeopleSnapshot,
  freshCivilPapersPeople,
) => {
  const existingIds = new Set(
    civilPapersPeopleSnapshot
      .map((p) => p?.civilPaperDefendantId)
      .filter(isNonEmptyVariable),
  );
  const claimedIds = new Set();
  const findByJacketId = (p) => {
    if (!isNonEmptyVariable(p?.jacketId)) return undefined;
    const found = freshCivilPapersPeople.find(
      (fp) =>
        !fp?.personIsBusiness &&
        fp?.jacketId === p.jacketId &&
        !existingIds.has(fp?.civilPaperDefendantId) &&
        !claimedIds.has(fp?.civilPaperDefendantId),
    );
    if (found) claimedIds.add(found.civilPaperDefendantId);
    return found;
  };

  return (p) => {
    if (isNonEmptyVariable(p?.civilPaperDefendantId)) {
      return freshCivilPapersPeople.find(
        (fp) => fp?.civilPaperDefendantId === p.civilPaperDefendantId,
      );
    }
    if (p?.personIsBusiness) {
      return freshCivilPapersPeople.find(
        (fp) =>
          isNonEmptyVariable(p?.personToServeBusinessId) &&
          fp?.personToServeBusinessId === p.personToServeBusinessId,
      );
    }
    const byBioOrJacket = isNonEmptyVariable(p?.bioId)
      ? freshCivilPapersPeople.find((fp) => fp?.bioId === p.bioId)
      : findByJacketId(p);
    if (byBioOrJacket || !p?.attorney?.[0]) return byBioOrJacket;

    // An Attorney-type person may carry its bio only on the attorney link
    // row, and a brand-new one has no bioId/jacketId anywhere locally — fall
    // back to the attorney's own bio, then to the first fresh attorney person
    // that wasn't on the record before this save.
    const attorneyBioId = p.attorney[0]?.bioId ?? p.attorney[0]?.bio?.[0]?.bioId;
    const found = isNonEmptyVariable(attorneyBioId)
      ? freshCivilPapersPeople.find(
          (fp) => fp?.attorney?.[0]?.bioId === attorneyBioId,
        )
      : freshCivilPapersPeople.find(
          (fp) =>
            isNonEmptyVariable(fp?.attorney?.[0]?.attorneyId) &&
            !existingIds.has(fp?.civilPaperDefendantId) &&
            !claimedIds.has(fp?.civilPaperDefendantId),
        );
    if (found) claimedIds.add(found.civilPaperDefendantId);
    return found;
  };
};

const saveNewCivilPapersPeopleExtras = async (
  civilPapersPeopleSnapshot,
  freshCivilPapersPeople,
) => {
  if (
    !Array.isArray(civilPapersPeopleSnapshot) ||
    !civilPapersPeopleSnapshot.length ||
    !Array.isArray(freshCivilPapersPeople) ||
    !freshCivilPapersPeople.length
  ) {
    return;
  }

  const findMatch = createFreshPersonMatcher(
    civilPapersPeopleSnapshot,
    freshCivilPapersPeople,
  );

  const saves = civilPapersPeopleSnapshot
    .filter(
      (p) =>
        !isNonEmptyVariable(p?.civilPaperDefendantId) &&
        isNonEmptyVariable(p?.civilPapersPeopleExtra?.[0]),
    )
    .map((p) => {
      const match = findMatch(p);
      if (!isNonEmptyVariable(match?.civilPaperDefendantId)) return null;
      return saveExtraTableRecord({
        tableName: "CivilPapersPeopleExtra",
        idColumn: "civilPaperDefendantID",
        idValue: match.civilPaperDefendantId,
        ...buildCamelCaseExtraPayload(p.civilPapersPeopleExtra[0]),
      });
    })
    .filter(Boolean);

  if (!saves.length) return;
  const results = await Promise.allSettled(saves);
  results.forEach((result) => {
    if (result.status === "rejected") {
      console.error("[saveNewCivilPapersPeopleExtras] save failed:", result.reason);
    }
  });
};

// Saves AttorneyExtra ("Additional Attorney Information") for each person's
// linked Attorney (CivilPaperAttorneyDetail.jsx). Same disclosed limitation
// as saveCivilPapersPeopleExtra — only fires once the Attorney record
// already has a real attorneyId.
// freshCivilPapersPeople (optional): the refetched people, used to resolve an
// attorneyId that only exists after the civil paper save.
const saveCivilPaperAttorneyExtra = async (
  civilPapersPeopleSnapshot,
  freshCivilPapersPeople = null,
) => {
  if (!Array.isArray(civilPapersPeopleSnapshot) || !civilPapersPeopleSnapshot.length) {
    return;
  }

  const findMatch = Array.isArray(freshCivilPapersPeople)
    ? createFreshPersonMatcher(civilPapersPeopleSnapshot, freshCivilPapersPeople)
    : () => undefined;

  const saves = civilPapersPeopleSnapshot
    .filter((p) => isNonEmptyVariable(p?.attorney?.[0]?.attorneyExtra?.[0]))
    .map((p) => {
      const attorneyId =
        p.attorney[0].attorneyId ?? findMatch(p)?.attorney?.[0]?.attorneyId;
      if (!isNonEmptyVariable(attorneyId)) return null;
      return saveExtraTableRecord({
        tableName: "AttorneyExtra",
        idColumn: "attorneyID",
        idValue: attorneyId,
        ...buildCamelCaseExtraPayload(p.attorney[0].attorneyExtra[0]),
      });
    })
    .filter(Boolean);

  if (!saves.length) return;
  const results = await Promise.allSettled(saves);
  results.forEach((result) => {
    if (result.status === "rejected") {
      console.error("[saveCivilPaperAttorneyExtra] save failed:", result.reason);
    }
  });
};

// Saves BusinessesExtra ("Additional Business Information") for both the
// Case Association business branch (CaseAssociationBusinessInformation.jsx
// — person.businesses[0], the business actually being served, keyed by
// personToServeBusinessId — confirmed against a real GET response to be a
// separate record from person.jacketBusiness, which this never reads) and
// each Attorney's own Business Details (CivilPaperAttorneyDetail.jsx —
// person.attorney) — both point at the same BusinessesExtra table, keyed by
// businessID. businesses[0] only carries a real businessId once a business
// has actually been picked via Business Search (see
// BusinessSearchFolderModal.jsx's mapRow); Attorney's business has no
// search wired at all (matches ASP), so that half realistically never fires
// until there's a real save round-trip to resolve an id from.
// freshCivilPapersPeople (optional): the refetched people, used to resolve a
// businessId that only exists after the civil paper save.
const saveCivilPaperBusinessesExtra = async (
  civilPapersPeopleSnapshot,
  freshCivilPapersPeople = null,
) => {
  if (!Array.isArray(civilPapersPeopleSnapshot) || !civilPapersPeopleSnapshot.length) {
    return;
  }

  const findMatch = Array.isArray(freshCivilPapersPeople)
    ? createFreshPersonMatcher(civilPapersPeopleSnapshot, freshCivilPapersPeople)
    : () => undefined;

  const saves = civilPapersPeopleSnapshot
    .flatMap((p) => {
      const match = findMatch(p);
      return [
        { source: p?.businesses?.[0], fresh: match?.businesses?.[0] },
        { source: p?.attorney?.[0], fresh: match?.attorney?.[0] },
      ];
    })
    .filter(({ source }) => isNonEmptyVariable(source?.businessesExtra?.[0]))
    .map(({ source, fresh }) => {
      const businessId = source.businessId ?? fresh?.businessId;
      if (!isNonEmptyVariable(businessId)) return null;
      return saveExtraTableRecord({
        tableName: "BusinessesExtra",
        idColumn: "businessID",
        idValue: businessId,
        ...buildCamelCaseExtraPayload(source.businessesExtra[0]),
      });
    })
    .filter(Boolean);

  if (!saves.length) return;
  const results = await Promise.allSettled(saves);
  results.forEach((result) => {
    if (result.status === "rejected") {
      console.error("[saveCivilPaperBusinessesExtra] save failed:", result.reason);
    }
  });
};

export {
  normalizeExtraValue,
  fetchExtraTableRecord,
  saveExtraTableRecord,
  saveBioAndArrestReportsExtra,
  saveInmatePropertyExtra,
  saveInmateMedicalExtra,
  saveBookingBailsExtra,
  saveHoldsExtra,
  saveInmateMovementRulesExtra,
  saveInmateTrackingExtra,
  saveInmatePropertyItemsExtra,
  saveAppointmentLogExtra,
  saveBioAddressesExtra,
  saveBusinessesExtra,
  saveCashAccountingNewExtra,
  saveCashAccountingNewTransactionsExtra,
  saveMedicationLogExtra,
  saveScheduledEventsExtra,
  saveStaffExtra,
  saveVictimNotificationExtra,
  saveVictimNotificationLogExtra,
  fetchAndMergeBookingExtras,
  saveCivilPapersNewExtra,
  saveTrustAccountingNewExtra,
  saveCivilPapersPeopleExtra,
  saveNewCivilPapersPeopleExtras,
  saveCivilPaperAttorneyExtra,
  saveCivilPaperBusinessesExtra,
};
